import { isEmpty } from 'lodash-es';

import { IHeaderBidder } from '~/src/dash/api/console-api';
import { IDeal } from '~/src/dash/api/deals';
import { ECurrencyType } from '~/src/dash/api/exchange-rates';
import { createUrl, createUrlWithParam, restGet, restPatch, restPost } from '~/src/dash/api/rest';
import {
  EPlatformConnectionActionType,
  IDemandSourcesIDAndName,
  IGetConnectionsResponse,
  IGetDealExtSellerPublishersResponse,
  IGetPendingOffersResponse,
  IGetPlatformConnectAPIParams,
  IGetUniqueDemandOpportunitiesResponse,
  IPlatformConnect,
  IPlatformConnectionOfferHistory,
  IPlatformConnectionWithNamesAndType,
} from '~/src/dash/views/platform-connect/common';

// UI key -> API key
const platformConnectAPIKeyMap: { [key: string]: string } = {
  page: 'page',
  pageSize: 'page_size',
};

export const fetchPendingOffers = async (
  publisherID: string,
  params: IGetPlatformConnectAPIParams,
): Promise<IGetPendingOffersResponse> => {
  const query = Object.entries(params).map(
    ([key, val]) =>
      `&${platformConnectAPIKeyMap[key]}=${Array.isArray(val) ? val.join(',') : String(val)}`,
  );
  const reqUrl = `${createUrl('/platform_connect_pending_offers', publisherID)}${query.join('')}`;

  try {
    const response = await restGet<any>(reqUrl);
    const { parsedBody } = response;
    return {
      PendingOffers: parsedBody?.PendingOffers || [],
      TotalRows: parsedBody?.TotalRows,
    } as IGetPendingOffersResponse;
  } catch (err) {
    throw err;
  }
};

export const fetchConnections = async (
  publisherID: string,
  params: IGetPlatformConnectAPIParams,
): Promise<IGetConnectionsResponse> => {
  const query = Object.entries(params).map(
    ([key, val]) =>
      `&${platformConnectAPIKeyMap[key]}=${Array.isArray(val) ? val.join(',') : String(val)}`,
  );
  const reqUrl = `${createUrl('/platform_connect_connections', publisherID)}${query.join('')}`;

  try {
    const response = await restGet<any>(reqUrl);
    const { parsedBody } = response;
    return {
      Connections: parsedBody?.Connections || [],
      TotalRows: parsedBody?.TotalRows,
    } as IGetConnectionsResponse;
  } catch (err) {
    throw err;
  }
};

export const fetchDeals = async (publisherID: string): Promise<IDeal[] | undefined> => {
  const reqUrl = createUrl('/deals/active', publisherID);

  try {
    const res = await restGet<IDeal[]>(reqUrl);
    return res.parsedBody as IDeal[];
  } catch (err) {
    throw err;
  }
};

export const fetchUniqueDemandOpportunities = async (
  publisherID: string,
  demandSources: IDemandSourcesIDAndName[],
  params: IGetPlatformConnectAPIParams,
): Promise<IGetUniqueDemandOpportunitiesResponse> => {
  const query = Object.entries(params).map(
    ([key, val]) =>
      `&${platformConnectAPIKeyMap[key]}=${Array.isArray(val) ? val.join(',') : String(val)}`,
  );
  const reqUrl = `${createUrl(
    '/platform_connect/unique_demand_opportunities',
    publisherID,
  )}${query.join('')}`;

  try {
    const response = await restPost<any>(reqUrl, { DemandSources: demandSources });
    const { parsedBody } = response;
    return {
      UniqueDemandOpportunities: parsedBody?.UniqueDemandOpportunities,
      TotalRows: parsedBody?.TotalRows,
    } as IGetUniqueDemandOpportunitiesResponse;
  } catch (err) {
    throw err;
  }
};

export const fetchSellerPublishers = async (
  publisherID: string,
  dealID: string,
  params: IGetPlatformConnectAPIParams,
): Promise<IGetDealExtSellerPublishersResponse> => {
  const query = Object.entries(params).map(
    ([key, val]) =>
      `&${platformConnectAPIKeyMap[key]}=${Array.isArray(val) ? val.join(',') : String(val)}`,
  );
  const reqUrl = `${createUrlWithParam(
    '/platform_connect/all_sellers',
    publisherID,
    dealID,
    'deal_id',
  )}${query.join('')}`;

  try {
    const response = await restGet<any>(reqUrl);
    const { parsedBody } = response;
    return {
      DealExtSellerPublishers: parsedBody?.DealExtSellerPublishers,
      TotalRows: parsedBody?.TotalRows,
    } as IGetDealExtSellerPublishersResponse;
  } catch (err) {
    throw err;
  }
};

export async function updatePlatformConnection(
  publisherID: string,
  updates: Partial<IPlatformConnect>,
  actionType: EPlatformConnectionActionType,
): Promise<any> {
  const reqUrl = createUrl('/platform_connection', publisherID);

  try {
    if (updates.BidFloor === undefined || (updates.BidFloor && updates.BidFloor > 0)) {
      const response = await restPatch<any>(reqUrl, {
        PlatformConnection: updates,
        ActionType: actionType,
      });
      return response.parsedBody;
    } else {
      throw new Error('Invalid bid floor.');
    }
  } catch (err) {
    throw err;
  }
}

export async function createPlatformConnection(
  publisherID: string,
  data: Partial<IPlatformConnectionWithNamesAndType>,
  buyerNote: string,
): Promise<any> {
  const reqUrl = `${createUrl('/platform_connection', publisherID, 'v1')}`;
  try {
    const response = await restPost<any>(reqUrl, {
      PlatformConnectionWithNamesAndType: data,
      note: buyerNote,
    });
    return response.parsedBody;
  } catch (err) {
    throw err;
  }
}

export async function createPlatformConnectionOfferHistory(
  publisherID: string,
  data: Partial<IPlatformConnectionOfferHistory>,
  buyerPublisherID: string,
  sellerPublisherID: string,
): Promise<any> {
  let reqUrl = `${createUrl('/platform_connection_offer_history', publisherID, 'v1')}`;
  reqUrl += '&buyer_publisher_id=' + String(buyerPublisherID);
  reqUrl += '&seller_publisher_id=' + String(sellerPublisherID);
  try {
    const response = await restPost<any>(reqUrl, data);
    return response.parsedBody;
  } catch (err) {
    throw err;
  }
}

export const fetchPlatformConnectionOfferHistory = async (
  publisherID: string,
  platformConnectionID: string,
  currencySymbol?: string,
  currencyType?: ECurrencyType,
): Promise<IPlatformConnectionOfferHistory[] | undefined> => {
  if (isEmpty(currencySymbol)) {
    currencySymbol = '$';
  }
  if (currencyType === undefined) {
    currencyType = ECurrencyType.CURRENCY_TYPE_USD;
  }

  let reqUrl = createUrlWithParam(
    '/platform_connection_offer_history',
    publisherID,
    platformConnectionID,
    'platform_connection_id',
  );
  if (currencySymbol) {
    reqUrl += '&currency_symbol=' + encodeURIComponent(currencySymbol);
  }
  if (currencyType) {
    reqUrl += '&currency_type=' + encodeURIComponent(String(currencyType));
  }

  try {
    const res = await restGet<any>(reqUrl);
    return res.parsedBody?.OfferHistories;
  } catch (err) {
    throw err;
  }
};

export async function createPlatformConnectionChannelAndBidder(
  publisherID: string,
  data: any,
): Promise<any> {
  const reqUrl = `${createUrl('/platform_connection_channel_and_bidder', publisherID, 'v1')}`;
  try {
    const response = await restPost<any>(reqUrl, data);
    return response.parsedBody;
  } catch (err) {
    throw err;
  }
}

export const fetchPlatformConnectionDemandSourceBidders = async (
  publisherID: string,
  demandSource: string,
): Promise<IHeaderBidder[] | undefined> => {
  const reqUrl = createUrlWithParam(
    '/platform_connection_demand_source_bidders',
    publisherID,
    demandSource,
    'demand_source',
  );

  try {
    const res = await restGet<any>(reqUrl);
    return res.parsedBody?.Bidders;
  } catch (err) {
    throw err;
  }
};

export const fetchHeaderBidderIDsBelongingToDeal = async (
  publisherID: string,
  dealID: number,
): Promise<IHeaderBidder[] | undefined> => {
  const reqUrl = createUrlWithParam(
    '/header_bidder_ids_belonging_to_deal',
    publisherID,
    dealID.toString(),
    'deal_id',
  );

  try {
    const res = await restGet<any>(reqUrl);
    return res.parsedBody?.Bidders;
  } catch (err) {
    throw err;
  }
};

export const platformConnectBuyerFinalConfirmation = async (
  publisherID: number,
  sellerPublisherID: number,
  platformConnectionID: number,
  platformConnectTargetedBidderIDs: string[],
  dealID: number,
  fromDealExtension: boolean, // is created by platform connection deal extension
  bidFloor: number,
  bidAdjustmentFactor: string,
  bidAdjustmentValue: string,
  adsTxt: string,
): Promise<any> => {
  let reqUrl = createUrl('/platform_connect_buyer_final_confirmation', String(publisherID));
  reqUrl += '&seller_publisher_id=' + String(sellerPublisherID);
  reqUrl += '&platform_connection_id=' + String(platformConnectionID);
  if (platformConnectTargetedBidderIDs.length > 0) {
    reqUrl += '&platform_connect_targeted_bidder_ids=' + platformConnectTargetedBidderIDs.join(',');
  }
  reqUrl += '&deal_id=' + String(dealID);
  if (fromDealExtension) {
    reqUrl += '&from_deal_extension=' + String(fromDealExtension);
  }
  reqUrl += '&bid_floor=' + String(bidFloor);
  reqUrl += '&bid_adjustment_factor=' + (!isEmpty(bidAdjustmentFactor) ? bidAdjustmentFactor : '1');
  reqUrl += '&bid_adjustment_value=' + (!isEmpty(bidAdjustmentValue) ? bidAdjustmentValue : '0');

  try {
    const res = (await restPost<string>(reqUrl, { AdsTxt: adsTxt })).parsedBody;
    return res;
  } catch (e) {
    console.error(e);
    return e;
  }
};
